import { useState } from 'react';
import { useNavigate, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Coins,
  Swords,
  Map,
  PlayCircle,
  Shield,
  ChevronRight,
  Trophy,
} from 'lucide-react';
import { GenericCard } from '@/components/molecules/GenericCard';
import { CatchCharges } from '@/components/molecules/CatchCharges';
import { PlayerStatsPanel } from '@/components/molecules/PlayerStatsPanel';
import { getMyTeam, getOwnedPokemons, getPokePlayerStats } from '@/services/pokemon.ts';
import { getPrestigeLeaderboard } from '@/services/user';
import { useMeUser } from '@/hooks/useUser.tsx';
import { userAvatarFallback } from '@/utils/userAvatar';
import { cdnUrl } from '@/utils/cdn';
import { TYPE_COLORS } from '@/utils/pokemonTypes';

const PRESTIGE_MEDAL: Record<number, string> = { 1: '🥇', 2: '🥈', 3: '🥉' };

const TOTAL_POKEMON = 1214;

const NAV_SECTIONS = [
  {
    to: '/pokemons',
    label: 'Pokémon',
    description: 'Ta collection',
    icon: Swords,
    gradient: 'from-primary-500 to-primary-600',
  },
  {
    to: '/map',
    label: 'Map',
    description: 'Le map minecraft',
    icon: Map,
    gradient: 'from-blue-500 to-blue-700',
  },
  {
    to: '/videos',
    label: 'Vidéos',
    description: 'Videos du bitume',
    icon: PlayCircle,
    gradient: 'from-red-500 to-red-700',
  },
  {
    to: '/role',
    label: 'Rôles',
    description: 'Tes rôles Discord',
    icon: Shield,
    gradient: 'from-purple-500 to-purple-700',
  },
];

export const Home = () => {
  const navigate = useNavigate();
  const { meUser } = useMeUser();
  const [hasAvatarError, setHasAvatarError] = useState(false);

  const { data: pokeStats } = useQuery({
    queryKey: ['mePlayerStats'],
    queryFn: getPokePlayerStats,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const { data: pokemons } = useQuery({
    queryKey: ['pokemons'],
    queryFn: () => getOwnedPokemons({ limit: 6 }),
    staleTime: 60_000,
  });

  const { data: myTeam } = useQuery({
    queryKey: ['myTeam'],
    queryFn: getMyTeam,
    staleTime: 30_000,
  });

  const { data: prestigeTop3 } = useQuery({
    queryKey: ['leaderboard-prestige'],
    queryFn: getPrestigeLeaderboard,
    staleTime: 5 * 60_000,
    select: data => data.slice(0, 3),
  });

  const capturedCount = pokeStats?.pokedexProg ?? 0;
  const progress = Math.round((capturedCount / TOTAL_POKEMON) * 100);
  const pokeDollars = pokeStats?.pokeDollars ?? 0;

  return (
    <main className="min-h-screen">
      <div className="mx-auto flex w-full max-w-5xl flex-col gap-5 p-4 sm:p-5">

        <section className="bg-warm dark:bg-raised overflow-hidden rounded-2xl shadow-sm border border-line-soft">
          <div className="p-5 sm:p-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {meUser && (
                  <div className="bg-primary-500/20 flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full ring-2 ring-line-mid sm:h-16 sm:w-16">
                    {meUser.avatarUrl && !hasAvatarError ? (
                      <img
                        src={meUser.avatarUrl}
                        alt={`Avatar de ${meUser.username}`}
                        className="h-full w-full object-cover"
                        onError={() => setHasAvatarError(true)}
                      />
                    ) : (
                      <span className="text-content font-lexend text-lg font-bold uppercase">
                        {userAvatarFallback(meUser.username)}
                      </span>
                    )}
                  </div>
                )}
                <div>
                  <p className="text-muted text-xs">Bienvenue,</p>
                  <h1 className="font-lexend text-content text-xl font-semibold sm:text-2xl">
                    {meUser?.username ?? '…'}
                  </h1>
                </div>
              </div>

              <div className="text-right">
                <p className="text-muted text-xs">PokéDollars</p>
                <p className="font-lexend text-content flex items-center justify-end gap-1.5 text-lg font-semibold">
                  <Coins size={16} className="text-primary-500" />
                  {pokeDollars.toLocaleString('fr-FR')}
                </p>
              </div>
            </div>
          </div>
        </section>
        <GenericCard title="Navigation rapide">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {NAV_SECTIONS.map(({ to, label, description, icon: Icon, gradient }) => (
                <button
                    key={to}
                    onClick={() => navigate(to)}
                    className={`group relative flex flex-col items-start gap-3 overflow-hidden rounded-2xl bg-linear-to-br p-4 ${gradient} text-left shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl active:scale-[0.98]`}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                    <Icon size={20} className="text-white" />
                  </div>
                  <div>
                    <p className="font-lexend text-sm font-semibold text-white">
                      {label}
                    </p>
                    <p className="mt-0.5 text-xs text-white/60">{description}</p>
                  </div>
                  <ChevronRight
                      size={16}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-white/60"
                  />
                </button>
            ))}
          </div>
        </GenericCard>

        <GenericCard
          title="Mes Pokémon"
          actions={
            <button
              onClick={() => navigate('/pokemons')}
              className="text-content/60 hover:text-primary-500 flex items-center gap-1 text-xs font-semibold transition-colors"
            >
              Voir tout <ChevronRight size={14} />
            </button>
          }
        >
          <div className="mb-4">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-content/60 text-xs font-semibold uppercase tracking-wide">
                Pokédex
              </span>
              <span className="font-lexend text-content text-sm font-semibold">
                {capturedCount}
                <span className="text-content/40 text-xs">/{TOTAL_POKEMON}</span>
              </span>
            </div>
            <div className="bg-primary-500/15 h-2.5 overflow-hidden rounded-full">
              <div
                className="from-primary-500 to-secondary-500 h-2.5 rounded-full bg-linear-to-r transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-content/40 mt-1 text-right text-xs">
              {progress}% complété
            </p>
          </div>

          <hr className="border-line-soft" />

          {pokeStats && (
            <div className="py-4">
              <CatchCharges pokeStats={pokeStats} flat />
            </div>
          )}

          {pokeStats && (
            <>
              <hr className="border-line-soft" />
              <div className="py-4">
                <PlayerStatsPanel stats={pokeStats} />
              </div>
            </>
          )}

          {myTeam && (
            <>
              <hr className="border-line-soft" />
              <div className="pt-4 pb-2">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-content/60 text-xs font-semibold uppercase tracking-wide">
                    Mon équipe ({myTeam.filter(s => s.pokemon).length}/6)
                  </span>
                  <button
                    onClick={() => navigate('/pokemons')}
                    className="text-content/60 hover:text-primary-500 flex items-center gap-1 text-xs font-semibold transition-colors"
                  >
                    Gérer <ChevronRight size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {myTeam.map((slot) => (
                    <button
                      key={slot.slot}
                      onClick={() => navigate('/pokemons')}
                      className="flex flex-col items-center gap-1"
                    >
                      <div className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 transition-colors ${
                        slot.pokemon
                          ? 'bg-primary-500/10 border-transparent hover:border-primary-300'
                          : 'border-dashed border-line-mid bg-raised'
                      }`}>
                        {slot.pokemon ? (
                          <img
                            src={cdnUrl(slot.pokemon.isShiny ? slot.pokemon.pokemon.spriteShiny : slot.pokemon.pokemon.sprite)}
                            alt={slot.pokemon.nickname}
                            className="h-10 w-10 object-contain"
                            onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                          />
                        ) : (
                          <span className="text-content/20 text-lg font-bold">{slot.slot}</span>
                        )}
                      </div>
                      {slot.pokemon && (
                        <span className="bg-secondary-500 rounded-full px-1.5 py-0.5 text-[9px] font-semibold text-white">
                          {slot.pokemon.level}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {pokemons && pokemons.data.length > 0 && (
            <>
              <hr className="border-line-soft" />
              <div className="grid grid-cols-3 gap-3 pt-4 sm:grid-cols-6">
                {pokemons.data.map((ownedPokemon) => {
                  if (!ownedPokemon.pokemon) return null;
                  const spriteUrl = cdnUrl(ownedPokemon.isShiny ? ownedPokemon.pokemon.spriteShiny : ownedPokemon.pokemon.sprite);
                  return (
                    <button
                      key={ownedPokemon.ownedPokemonId}
                      onClick={() => navigate('/pokemons')}
                      className="hover:border-primary-300 group relative flex flex-col items-center rounded-xl border border-transparent bg-raised p-2 transition-all duration-200 hover:shadow-md"
                    >
                      {ownedPokemon.isShiny && (
                        <span className="bg-primary-500 absolute top-1.5 right-1.5 rounded-full px-1 py-0 text-[9px] font-bold leading-tight text-white">
                          ★
                        </span>
                      )}
                      <div className="bg-primary-500/10 mb-1.5 flex h-12 w-12 items-center justify-center rounded-lg">
                        <img
                          src={spriteUrl}
                          alt={ownedPokemon.nickname}
                          loading="lazy"
                          className="h-10 w-10 object-contain transition-transform duration-200 group-hover:scale-110"
                          onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                        />
                      </div>
                      <p className="text-content font-lexend w-full truncate text-center text-xs font-semibold">
                        {ownedPokemon.nickname}
                      </p>
                      <div className="mt-1 flex flex-wrap justify-center gap-0.5">
                        {ownedPokemon.pokemon.types.slice(0, 1).map((typeName) => (
                          <span
                            key={typeName}
                            className={`${TYPE_COLORS[typeName] ?? 'bg-gray-400'} rounded px-1 py-0.5 text-[9px] font-semibold text-white capitalize`}
                          >
                            {typeName}
                          </span>
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </GenericCard>

        {/* ── Top 3 Prestige ── */}
        {prestigeTop3 && prestigeTop3.length > 0 && (
          <GenericCard
            title="Top 3 Prestige"
            subtitle="Score de prestige sur 1 000 points"
            actions={
              <Link
                to="/pokemons/leaderboard"
                className="text-content/60 hover:text-primary-500 flex items-center gap-1 text-xs font-semibold transition-colors"
              >
                Voir tout <ChevronRight size={14} />
              </Link>
            }
          >
            <div className="flex flex-col gap-2">
              {prestigeTop3.map(entry => {
                const isMe = entry.username === meUser?.username;
                return (
                  <div
                    key={entry.rank}
                    className={`flex items-center gap-3 rounded-2xl border bg-surfacepx-4 py-3 shadow-sm ${
                      isMe ? 'border-primary-300' : 'border-transparent'
                    }`}
                  >
                    <span className="text-xl leading-none">{PRESTIGE_MEDAL[entry.rank]}</span>
                    <p
                      className={`font-lexend min-w-0 flex-1 truncate font-semibold ${
                        isMe ? 'text-primary-500' : 'text-content'
                      }`}
                    >
                      {entry.username}
                      {isMe && (
                        <span className="ml-2 rounded-full bg-primary-500/15 px-2 py-0.5 text-[10px] font-bold text-primary-500">
                          Moi
                        </span>
                      )}
                    </p>
                    <div className="flex items-center gap-1.5 text-right">
                      <Trophy size={12} className="text-primary-400 shrink-0" />
                      <p className="font-lexend text-content font-bold">
                        {Math.round(entry.score)}
                        <span className="text-content/40 text-xs font-normal">/1000</span>
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </GenericCard>
        )}

      </div>
    </main>
  );
};